import { useMemo } from 'react'

import { SubscriptionLevel, type Post } from '@tribeplatform/gql-client/types'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon, type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { useToastContext } from '@tribeplatform/react-ui-kit/Toast'

import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'

/** null = not following */
export type FollowLevel = SubscriptionLevel | null

/** Sprite icon for each level; the menu rows and the follow buttons share it. */
export const followLevelIcons: Record<SubscriptionLevel, SvgIconName> = {
  [SubscriptionLevel.allReplies]: 'bell-ringing-01',
  [SubscriptionLevel.repliesToMe]: 'bell-01',
}

export const unfollowIcon: SvgIconName = 'bell-off-01'

export const getPostFollowLevel = (post: Post): FollowLevel => {
  if (!post?.authMemberProps?.subscribed) {
    return null
  }
  return post.authMemberProps.subscriptionLevel ?? SubscriptionLevel.allReplies
}

export const useFollowLevelLabels = () => {
  const { $t } = useI18n()
  return useMemo<Record<SubscriptionLevel, string>>(
    () => ({
      [SubscriptionLevel.allReplies]: $t({
        defaultMessage: 'All replies',
        id: 'Post.Follow.AllReplies',
      }),
      [SubscriptionLevel.repliesToMe]: $t({
        defaultMessage: 'Only replies to me',
        id: 'Post.Follow.RepliesToMe',
      }),
    }),
    [$t],
  )
}

export const UnfollowedToast = ({
  title,
  onUndo,
}: {
  title: string
  onUndo: () => void
}) => {
  const { $t } = useI18n()
  const { onClose } = useToastContext()

  return (
    <Alert
      status="neutral"
      withClose
      onClose={onClose}
      title={$t(
        {
          defaultMessage: "You're no longer following {post}.",
          id: 'Notifications.Unfollowed.Post',
        },
        { post: title },
      )}
    >
      <Alert.Actions>
        <Button
          size="sm"
          variant="secondaryNeutral"
          onClick={() => {
            onUndo()
            onClose()
          }}
        >
          <T defaultMessage="Undo" id="Generics.Undo" />
        </Button>
      </Alert.Actions>
    </Alert>
  )
}

interface FollowLevelMenuItemsProps {
  level: FollowLevel
  onSelect: (level: FollowLevel) => void
}

/** The follow rows of a dropdown; Unfollow only shows while following. */
export const FollowLevelMenuItems = ({
  level,
  onSelect,
}: FollowLevelMenuItemsProps) => {
  const { $t } = useI18n()
  const labels = useFollowLevelLabels()

  const item = (value: FollowLevel, label: string, icon: SvgIconName) => (
    <Dropdown.Item
      leadingIcon={<SvgIcon name={icon} />}
      trailingAddon={level === value ? <SvgIcon name="check" /> : undefined}
      onClick={() => onSelect(value)}
    >
      {label}
    </Dropdown.Item>
  )

  return (
    <>
      {item(
        SubscriptionLevel.allReplies,
        labels[SubscriptionLevel.allReplies],
        followLevelIcons[SubscriptionLevel.allReplies],
      )}
      {item(
        SubscriptionLevel.repliesToMe,
        labels[SubscriptionLevel.repliesToMe],
        followLevelIcons[SubscriptionLevel.repliesToMe],
      )}
      {level !== null &&
        item(
          null,
          $t({ defaultMessage: 'Unfollow', id: 'Post.Follow.Unfollow' }),
          unfollowIcon,
        )}
    </>
  )
}
